import PersonDropdown from "@/components/entities/app/person/partials/inputs/PersonDropdown";
import { PersonType } from "@/types/person-types";
import { gql } from "@apollo/client";
import If from "components/common/If";
import CheckboxInput from "components/common/form/inputs/CheckboxInput";
import Input from "components/common/form/inputs/Input";
import RadioButton, { RadioButtonInputPropsType } from "components/common/form/inputs/RadioButtonInput";
import PageForm, { PageFormProps } from "components/common/page/PageForm";
import { useState } from "react";
import { EventTemplateFormDataQuery, EventTemplateInput, SaveEventTemplateMutation, SaveEventTemplateMutationVariables } from "types/graphql";
import { EVENT_TEMPLATE_ON_DAY_CHECK_IN, EVENT_TEMPLATE_ON_DAY_CHECK_OUT, EVENT_TEMPLATE_ON_DAY_ROOM_CHANGE } from "utilities/entities/accommodation/event-template-utilities";



type Model = Omit<EventTemplateInput, 'onDay' | 'assigneeId'> & {
  isSystem: boolean;
  onDay: number | string;
  onDayType: number | 'day';
  assignee?: {
    id: number;
    name: string;
  }
}

const query = gql`
query EventTemplateFormData($id: Int) {
    eventTemplate(id: $id) {
      id
      name
      onDay
      repeat
      isSystem
      isActive
      changeDynamicly
      order,
      assignee {
        id
        name
      }
    }
  }
`;

const mutation = gql`
mutation SaveEventTemplate($model: EventTemplateInput!) {
    eventTemplate(model: $model) {
      id
    }
  }
`;

const EventTemplateFormPage = () => {
  const [model, setModel] = useState<Model>({
    id: 0,
    name: '',
    onDay: '',
    isActive: true,
    repeat: false,
    isSystem: false,
    onDayType: 'day',
    changeDynamicly: false,
    order: 10
  })

  const pageConfig: PageFormProps<EventTemplateFormDataQuery, SaveEventTemplateMutation, SaveEventTemplateMutationVariables> = {
    title: 'pages.accommodation.eventTemplate.form',
    name: 'eventTemplateForm',
    onSubmit: {
      mutation,
      resolveMutationData: () => ({
        model: {
          id: model.id,
          isActive: model.isActive,
          name: model.name,
          onDay: model.onDayType === 'day' ? Number(model.onDay) : Number(model.onDayType),
          repeat: model.onDayType === 'day' && model.repeat,
          changeDynamicly: model.changeDynamicly,
          order: Number(model.order),
          assigneeId: model.assignee?.id
        }
      }),
    },
    load: {
      query,
      resolveQueyData(data) {
        if (data?.eventTemplate) {
          setModel({
            ...data.eventTemplate,
            onDay: data!.eventTemplate!.onDay! > 0 ? data!.eventTemplate!.onDay! : '',
            onDayType: data!.eventTemplate!.onDay! > 0 ? 'day' : data!.eventTemplate!.onDay!,
            assignee: data!.eventTemplate!.assignee || undefined
          })
        }
      }
    }
  }

  const radioButtonConfig: RadioButtonInputPropsType<number | string> = {
    data: [EVENT_TEMPLATE_ON_DAY_CHECK_IN, EVENT_TEMPLATE_ON_DAY_CHECK_OUT, EVENT_TEMPLATE_ON_DAY_ROOM_CHANGE, 'day'],
    name: "onDayType",
    label: "fields.onDayType.name",
    value: model.onDayType,
    required: true,
    onChange: e => setDate('onDayType', e),
    display: (e) => {
      switch (e) {
        case EVENT_TEMPLATE_ON_DAY_CHECK_IN:
          return 'accommodation.eventTemplate.checkIn';
        case EVENT_TEMPLATE_ON_DAY_CHECK_OUT:
          return 'accommodation.eventTemplate.checkOut';
        case EVENT_TEMPLATE_ON_DAY_ROOM_CHANGE:
          return 'accommodation.eventTemplate.roomChange';
        default:
          return 'accommodation.eventTemplate.day';
      }
    }
  }

  const setDate = (property: keyof Model, value: any) => {
    let data = {
      ...model,
      [property]: value
    };
    if (property === 'onDayType' && value !== 'day') {
      data = {
        ...data,
        repeat: false
      }
    }
    setModel(data);
  }

  return (
    <PageForm {...pageConfig} >
      <fieldset>
        <Input
          name="name"
          label="fields.name.name"
          required
          disabled={model.isSystem}
          value={model.name}
          onChange={e => setDate('name', e || '')}
        />
      </fieldset>
      <If condition={!model.isSystem}>
        <fieldset>
          <PersonDropdown
            name="assignee"
            label="fields.assignee.name"
            disabled={model.isSystem}
            value={(model.assignee || undefined) as PersonType}
            onChange={e => setDate('assignee', e)}
          />
        </fieldset>
        <fieldset>
          <div>
            <RadioButton
              {...radioButtonConfig}
              value={model.onDayType}
            />
          </div>
          <div>
            <If condition={model.onDayType === 'day'}>
              <Input
                name="onDay"
                type="number"
                label="fields.onDay.name"
                required
                disabled={model.isSystem}
                value={model.onDay.toString()}
                min={1}
                onChange={e => setDate('onDay', e || '')}
              />
              <CheckboxInput
                name="repeat"
                label="fields.repeat.name"
                disabled={model.isSystem}
                value={model.repeat}
                onChange={e => setDate('repeat', e || false)}
              />
              <If condition={model.repeat}>
                <CheckboxInput
                  name="changeDynamicly"
                  label="fields.changeDynamicly.name"
                  disabled={model.isSystem}
                  value={model.changeDynamicly}
                  onChange={e => setDate('changeDynamicly', e || false)}
                />
              </If>
            </If>
          </div>
        </fieldset>
      </If>
      <fieldset>
        <CheckboxInput
          name="isActive"
          label="fields.isActive.name"
          value={model.isActive}
          onChange={e => setDate('isActive', e || false)}
        />
        <Input
          name="order"
          type="number"
          step={0}
          required
          label="fields.order.name"
          value={model.order.toString()}
          onChange={v => setDate('order', v)}
        />
      </fieldset>
    </PageForm>
  );
};

export default EventTemplateFormPage;
